import classNames from 'classnames';

import styles from './Callout.module.css';

import { Text } from '../Text';

export type CalloutVariants =
  | 'promo'
  | 'positive'
  | 'neutral'
  | 'negative'
  | 'info'
  | 'warning';

type IntrinsicDivProps = JSX.IntrinsicElements['div'];

export interface CalloutProps extends IntrinsicDivProps {
  /** Change the appearance of the Callout. */
  variant?: CalloutVariants;
  /**
   * The icon should be an SVG icon from the `@farmersdog/corgi-x/icons`
   * package, with a size prop of 20.
   *
   * @example `<CircleAlert size={20} />`
   */
  icon?: React.ReactElement;
  /** The main content of the Callout. Can be a string or ReactNode. */
  children: React.ReactNode;
  /** Class name applied to the Callout's container. */
  className?: string;
  /** Additional styles. */
  style?: React.CSSProperties;
}

function isString(node: React.ReactNode): node is string {
  return typeof node === 'string';
}

/**
 * Render a Callout to surface information that is timely, critical and
 * sometimes conditional.
 */
export function Callout(props: CalloutProps) {
  const { children, variant = 'neutral', icon, className, style } = props;
  return (
    <div
      role="note"
      className={classNames(styles.root, styles[variant], className)}
      style={style}
    >
      {icon && (
        <div aria-hidden className={classNames(styles.icon)}>
          {icon}
        </div>
      )}

      <div>
        {isString(children) ? (
          <Text as="p" vSpacing="none" className={styles[variant]}>
            {children}
          </Text>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
